import images from '@assets/images'
import AssessmentKSCharts from '@components/Charts/AssessmentKSCharts'
import { useQuery } from '@tanstack/react-query'
import { Flex, Spin } from 'antd'
import { useEffect, useState } from 'react'

import { Link } from '@suntex.core/nav'
import { Entities_Assessment_GoalsInfoKAS_Response } from '@suntex.fim/api-client'
import {
  $EntityLevel,
  $KnowAndShowCategory,
  $KnowAndShowLevel,
  EntityLevel,
  KnowAndShowLevel,
  SpecialGradeLevel
} from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { useEntityContext } from '@suntex.fim/nav'
import { Button, LevelSelect, Modal, Tooltip } from '@suntex.fim/ui'

const levelOptions = $KnowAndShowLevel.listOptions()
const categories = $KnowAndShowCategory.describeAll()

// The API returns KsData as { "1": { "1": 100, "2": 75 }, "2": { ... } } (2-level)
// despite the TS type claiming 3 levels.
const getFormattedData = (
  data: Entities_Assessment_GoalsInfoKAS_Response['ks']
) => {
  const chartData = Object.entries(data || {}).reduce(
    (newData, [key, value]) => {
      const levelValue = Object.entries(
        value as unknown as Record<string, number>
      ).reduce(
        (iNewData, [iKey, iValue]) => ({
          ...iNewData,
          [`ks_${key}_${iKey}`]: iValue
        }),
        {}
      )

      return { ...newData, ...levelValue }
    },
    {} as Record<string, number>
  )
  const firstLevel = data?.['1'] as unknown as
    | Record<string, number>
    | undefined
  const secondLevel = data?.['2'] as unknown as
    | Record<string, number>
    | undefined
  if (firstLevel && secondLevel) {
    const averageData = [1, 2, 3, 4].reduce(
      (newData, key) => {
        const val1 = firstLevel[String(key)] || 0
        const val2 = secondLevel[String(key)] || 0
        return {
          ...newData,
          [`ks_0_${key}`]: (val1 + val2) / 2
        }
      },
      {} as Record<string, number>
    )
    return { ...chartData, ...averageData }
  }

  return chartData
}

function KnowAndShow({
  id,
  year,
  grade,
  defaultGrade,
  entityLevel,
  kasLevel,
  onLevelChange
}: {
  id?: number
  year?: string
  grade?: SpecialGradeLevel
  defaultGrade?: SpecialGradeLevel
  entityLevel?: number
  kasLevel?: KnowAndShowLevel | null
  onLevelChange?: (level: KnowAndShowLevel) => void
}) {
  const { api } = useFramework()
  const [level, setLevel] = useState<KnowAndShowLevel>(
    kasLevel ?? KnowAndShowLevel.KS1
  )
  const [knowAndShowModal, setKnowAndShowModal] = useState(false)
  const { entityLevel: apiLevel, entityId: apiId } = useEntityContext()

  const role = $EntityLevel.describe(apiLevel).slug

  const { data, isLoading } = useQuery({
    queryKey: ['assessment', 'goalsInfoKas', apiId, apiLevel, year, grade],
    queryFn: async () =>
      (
        await api.get_Assessment_GoalsInfoKas({
          id: apiId!,
          level: apiLevel,
          year: year!,
          grade: grade!
        })
      ).ks,
    enabled: apiId != null && year != null && grade != null
  })

  useEffect(() => {
    if (onLevelChange) {
      onLevelChange(level) // Notify parent of level changes
    }
  }, [level, onLevelChange])

  useEffect(() => {
    const g = defaultGrade ?? grade
    if (g != null && kasLevel === null && apiLevel !== EntityLevel.School) {
      setLevel($KnowAndShowLevel.fromOptional(g) ?? KnowAndShowLevel.KS1)
    }
  }, [defaultGrade, grade, kasLevel, apiLevel])
  return (
    <Flex vertical gap="20px" className="graph-tiles team-activity">
      <Flex
        align="center"
        className="graph-tiles__title"
        justify="space-between"
      >
        <Flex gap="10px">
          {entityLevel !== EntityLevel.Player ? (
            <Link
              className="report-link"
              to={`/${role}/${id}/assessment-report/know-and-show`}
              state={{ level }}
            >
              <h4 className="title">Know & Show - Word Problem Fluency</h4>
            </Link>
          ) : (
            <h4 className="title">Know & Show - Word Problem Fluency</h4>
          )}
          <Button
            aria-label="Know and Show information"
            intent="icon"
            onClick={() => setKnowAndShowModal(true)}
          >
            <images.InfoCircle />
          </Button>
        </Flex>
        <div className="right">
          {entityLevel !== EntityLevel.Player && (
            <Link
              className="print-hidden"
              to={`/${role}/${id}/assessment-report/know-and-show`}
              state={{ level }}
            >
              <Tooltip
                classNames={{ root: 'progress-tooltip' }}
                title={
                  <div>
                    Click here to drill down into the Know And Show report for
                    this {role}
                  </div>
                }
                arrow={false}
                placement="bottom"
              >
                <img
                  src={images.Wpf}
                  alt="Wpf"
                  className="goal-index-report-icon"
                />
              </Tooltip>
            </Link>
          )}
          <LevelSelect
            value={level}
            onChange={(level) => setLevel(level)}
            options={[...levelOptions]}
          />
        </div>
      </Flex>
      <Flex vertical align="left">
        <div className="graph-container">
          {isLoading ? (
            <Spin />
          ) : (
            data && (
              <AssessmentKSCharts
                backgroundColor="#83E2B8"
                level={level}
                chartData={getFormattedData(data)}
              />
            )
          )}
        </div>
      </Flex>
      {knowAndShowModal && (
        <Modal
          centered
          open
          onOk={() => setKnowAndShowModal(false)}
          onCancel={() => setKnowAndShowModal(false)}
          width={900}
          className="know-and-show-modal"
          footer={[
            <Button
              shape="round"
              intent="tertiary"
              key="submit"
              onClick={() => setKnowAndShowModal(false)}
            >
              Close
            </Button>
          ]}
        >
          <div className="word-problem-container">
            <h2
              style={{
                color: '#1a73e8',
                fontSize: '1.5em',
                marginBottom: '10px'
              }}
            >
              Word Problem Categories
            </h2>
            <ul>
              {categories.map((category) => (
                <li key={category.label}>
                  <strong>{category.label}</strong> - {category.skills}
                </li>
              ))}
            </ul>
          </div>
        </Modal>
      )}
    </Flex>
  )
}

export default KnowAndShow
